import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CommonFieldService} from '../../../../services/common/common-field.service';
import {InternalTradeOrderViewService} from '../../../../services/internal/internal-trade-order-view.service';
import {finalize, Observable, switchMap, take, takeUntil, timer} from "rxjs";
import {ApiValidation} from "../../../../model/api/api-validation";
import {ClearingSelectionInfo} from "../../../../model/clearing/clearing-selection-info";
import {ClearingAllocationService} from "../../../../services/clearing/clearing-allocation.service";
import {ClearingAllocationTaskService} from "../../../../services/clearing/clearing-allocation-task.service";
import {
  UpdateSingleAllocationFormComponent
} from "../form/allocation/update-single-allocation-form/update-single-allocation-form.component";
import {
  UpdateSplitAllocationFormComponent
} from "../form/allocation/update-split-allocation-form/update-split-allocation-form.component";
import {
  ExchangeApsOverrideFormComponent
} from "../form/exchange-aps-override-form/exchange-aps-override-form.component";
import {OrderDetailComponent} from "./order-detail/order-detail.component";
import {ThemeService} from "../../../../services/tools/theme.service";
import {ClearingOrderInfoService} from "../../../../services/clearing/clearing-order-info.service";
import {ClearingInfoLoadRequest} from "../../../../model/clearing/clearing-info-load-request";
import {OrderTableBaseComponent} from "./order-table-base.component";
import {AppConfigService} from "../../../../services/app-config.service";
import {WebTradeOrderView} from "../../../../model/internal/web-trade-order-view";
import {ClearingCookieService} from "../../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../../services/clearing/clearing-selection.service";
import {ClearingOrderWsService} from "../../../../services/clearing/ws/clearing-order-ws.service";
import {ClearingTableSyncWsService} from "../../../../services/clearing/ws/clearing-table-sync-ws.service";
import {ClearingTableConfigService} from "../../../../services/clearing/clearing-table-config.service";

@Component({
  selector: 'app-clearing-order-table[userView]',
  templateUrl: './order-table.component.html',
  styleUrls: ['../clearing-table-base.component.scss', 'order-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderTableComponent extends OrderTableBaseComponent {


  static WAIT_FOR_APPROVAL_STATE: string = 'WAIT FOR APPROVAL';
  static GIVEUP_TO_BE_CONFIRMED_STATE: string = 'GIVEUP TO BE CONFIRMED';
  static ACCEPTED_GIVEUP_STATE: string = 'ACCEPTED GIVEUP';
  static REVERSAL_REQUESTED_STATE: string = 'GIVE-UP REVERSAL REQUESTED';
  static ERROR_STATE: string = 'ERROR';

  @ViewChild('orderDetailDialog')
  orderDetailDialog: OrderDetailComponent;

  @ViewChild('updateAllocationDialog')
  updateAllocationDialog: UpdateSingleAllocationFormComponent;

  @ViewChild('exchangeApsOverrideDialog')
  exchangeApsOverrideDialog: ExchangeApsOverrideFormComponent;

  @ViewChild('updateSplitAllocationDialog')
  updateSplitAllocationDialog: UpdateSplitAllocationFormComponent;

  constructor(private el: ElementRef
    , cdref: ChangeDetectorRef,
              protected allocationService: ClearingAllocationService,
              allocationTaskService: ClearingAllocationTaskService,
              protected commonFieldService: CommonFieldService,
              protected userViewService: InternalTradeOrderViewService,
              appConfigService: AppConfigService,
              clearingOrderWsService: ClearingOrderWsService,
              messageService: MessageService,
              protected confirmationService: ConfirmationService,
              translateService: TranslateService,
              public themeService: ThemeService,
              protected clearingOrderInfoService: ClearingOrderInfoService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService) {
    super(cdref,
      allocationService,
      allocationTaskService,
      commonFieldService,
      userViewService,
      appConfigService,
      clearingOrderWsService,
      messageService,
      confirmationService,
      translateService,
      themeService,
      tableConfigService,
      clearingCookieService,
      clearingTableSyncWsService,
      clearingSelectionService);
    this.origin = WebTradeOrderView.ORIGIN.ORDER;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public refreshTable() {
    super.refreshTable();
    if (!this.tableConfig) {
      return;
    }
    let configToAdjust = this.tableConfig.find(value => value.field === 'reference');
    if (configToAdjust) {
      configToAdjust.mapId = undefined;
    }
    configToAdjust = this.tableConfig.find(value => value.field === 'type');
    if (configToAdjust)
      configToAdjust.mapId = undefined;
    configToAdjust = this.tableConfig.find(value => value.field === 'tradeType');
    if (configToAdjust)
      configToAdjust.mapId = undefined;
    configToAdjust = this.tableConfig.find(value => value.field === 'volume');
    if (configToAdjust)
      configToAdjust.mapId = undefined;
    configToAdjust = this.tableConfig.find(value => value.field === 'overrideAvgPrice');
    if (configToAdjust)
      configToAdjust.mapId = (record => ((record.overrideAvgPrice == AppConfigService.NO_OVERRIDE_APS_VALUE
        || record.overrideAvgPrice == 0) ? ''
        : (record.overrideAvgPrice == AppConfigService.OVERRIDE_TO_ZERO ? '0' : record.overrideAvgPrice)));
    this.markForCheck();
  }

  protected refreshButtons() {
    this.headerButtonConfig = [
      {
        tooltip: this.getTranslation('page.' + this.transPartKey + '.table.zoom_order'),
        icon: 'fas fa-magnifying-glass-plus',
        class: ' p-button-info',
        command: () => this.showDetail(),
        enabled: () => this.showDetailEnabled()
      }];
    this.tableContextMenuConfig = [];
    if (!this.isReadOnly()) {
      const apsManagementItems = [{
        id: 'apsmanagementapproveleg',
        label: this.getTranslation('page.' + this.transPartKey + '.table.approve_aps_leg_send_gu_no_override'),
        icon: 'fas fa-check',
        command: event => this.approveAPSLeg()
      }, {
        id: 'apsmanagementapprovewhole',
        label: this.getTranslation('page.' + this.transPartKey + '.table.approve_aps_exchange_override'),
        icon: 'fas fa-check',
        command: event => this.approveAPSWholeOrder()
      }, {
        separator: true
      }, {
        id: 'apsmanagementoverride',
        label: this.getTranslation('page.' + this.transPartKey + '.table.exchange_aps_override'),
        icon: 'fas fa-calculator',
        command: event => this.exchangeApsOverride()
      }];

      const reversalManagementItems = [{
        id: 'reversalmanagementreqgurev',
        label: this.getTranslation('page.' + this.transPartKey + '.table.request_reversal'),
        icon: 'fas fa-play',
        command: event => this.requestReversal()
      }, {
        id: 'reversalmanagementcancelreqgurev',
        label: this.getTranslation('page.' + this.transPartKey + '.table.cancel_requested_reversal'),
        icon: 'fas fa-stop',
        command: event => this.cancelReversal()
      }];
      this.headerButtonConfig.push(
        {
          separator: true,
        },
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.update_split_allocation'),
          'icon': 'fas fa-users',
          class: ' p-button-success',
          command: records => this.updateSplitAllocation(),
          enabled: () => this.updateSplitAllocationEnabled(this.currentClearingSelectionInfosByLeg)
        },
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.update_allocation'),
          'icon': 'fas fa-pen',
          class: ' p-button-success',
          command: records => this.updateAllocation(),
          enabled: () => this.updateAllocationEnabled()
        },
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.aps_management'),
          icon: 'fas fa-calculator',
          class: ' p-button-help cc-table-menu',
          id: 'apsmanagement',
          enabled: () => this.apsManagementEnabled(),
          items: apsManagementItems
        },
        {
          separator: true,
        });
      if (this.canDoReversal()) {
        this.headerButtonConfig.push(
          {
            tooltip: this.getTranslation('page.' + this.transPartKey + '.table.reversal_management'),
            icon: 'fas fa-backward',
            class: ' p-button-warning cc-table-menu',
            id: 'reversalmanagement',
            enabled: () => {
              return this.reversalManagementEnabled(OrderTableComponent.ACCEPTED_GIVEUP_STATE, OrderTableComponent.REVERSAL_REQUESTED_STATE)
            },
            items: reversalManagementItems
          });
      }
      this.headerButtonConfig.push(
        {
          tooltip: this.getTranslation('common.table.delete_button'),
          icon: 'fas fa-trash-alt',
          class: ' p-button-danger',
          command: () => this.deleteOrders(),
          enabled: () => this.deleteAllocationEnabled()
        },
        {
          id: 'disableAllocation',
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.disable_order'),
          icon: 'fas fa-circle-xmark',
          class: ' p-button-warning',
          command: () => this.disableOrder(),
          enabled: () => this.disableOrderEnabled()
        }
      );

      this.tableContextMenuConfig.push(
        {
          id: 'updateSplitAllocation',
          label: this.getTranslation('page.' + this.transPartKey + '.table.update_split_allocation'),
          icon: 'fas fa-users',
          command: () => this.updateSplitAllocation()
        },
        {
          id: 'updateAllocation',
          label: this.getTranslation('page.' + this.transPartKey + '.table.update_allocation'),
          icon: 'fas fa-pen',
          command: () => this.updateAllocation()
        },
        {
          id: 'apsmanagement',
          label: this.getTranslation('page.' + this.transPartKey + '.table.aps_management'),
          icon: 'fas fa-calculator',
          items: apsManagementItems
        },
        {
          separator: true,
        });

      if (this.canDoReversal()) {
        this.tableContextMenuConfig.push(
          {
            id: 'reversalmanagement',
            label: this.getTranslation('page.' + this.transPartKey + '.table.reversal_management'),
            icon: 'fas fa-backward',
            items: reversalManagementItems
          },);
      }
      this.tableContextMenuConfig.push(
        {
          id: 'deleteAllocation',
          label: this.getTranslation('common.table.delete_button'),
          icon: 'fas fa-trash-alt',
          command: () => this.deleteOrders()
        },
        {
          id: 'disableAllocation',
          label: this.getTranslation('page.' + this.transPartKey + '.table.disable_order'),
          icon: 'fas fa-circle-xmark',
          command: () => this.disableOrder()
        },
        {
          separator: true,
        });
    }
    this.tableContextMenuConfig.push(
      {
        id: 'showDetail',
        label: this.getTranslation('page.' + this.transPartKey + '.table.zoom_order'),
        icon: 'fas fa-magnifying-glass',
        command: () => this.showDetail()
      },
    );
    this.markForCheck();
  }

  deleteOrders() {
    const currentSelectedIds = [...this.selectedIds];
    const header = this.getTranslation('common.table.delete_item')
      + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + (currentSelectedIds.length === 1 ? '' : 's');
    const message = currentSelectedIds.length > 1 ? (this.getTranslation('common.dialog.confirm_delete_plu')
        + ' ' + this.getTranslation('page.' + this.transPartKey + '.item_name') + 's?')
      : (this.getTranslation('common.dialog.confirm_delete_sing') + ' '
        + this.getTranslation('page.' + this.transPartKey + '.item_name') + '?');
    this.confirmCommand(() => {
      this.allocationService.deleteOrders(currentSelectedIds).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
        this.checkAllocationTask(apiValidation);
      });
    }, header, message, 'common.dialog.delete');
  }

  approveAPSLeg() {
    if (this.apsManagementEnabled()) {
      const currentSelectedIds = [...this.selectedIds];
      this.confirmCommand(() => {
          this.allocationService.approveAPSLeg(currentSelectedIds[0]).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.approve_aps_leg_send_gu_no_override_title',
        'page.' + this.transPartKey + '.table.approve_aps_leg_send_gu_no_override_confirm',
        'common.dialog.approve');
    }
  }

  approveAPSWholeOrder() {
    if (this.apsManagementEnabled()) {
      const currentSelectedIds = [...this.selectedIds];
      this.confirmCommand(() => {
          this.allocationService.approveAPSWholeOrder(currentSelectedIds[0]).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.approve_aps_exchange_override_title',
        'page.' + this.transPartKey + '.table.approve_aps_exchange_override_confirm',
        'common.dialog.approve');
    }
  }

  disableOrder() {
    if (this.disableOrderEnabled()) {
      const currentSelectedIds = [...this.selectedIds];
      // const currentClearingSelectionInfos: ClearingSelectionInfo[] = [...this.currentClearingSelectionInfos];
      const currentClearingSelectionInfos: ClearingSelectionInfo[] = [...this.currentClearingSelectionInfosByLeg];
      const isDisabled = currentClearingSelectionInfos[0].disabled == true;

      this.confirmCommand(() => {
          this.allocationService.disableOrder(currentSelectedIds, isDisabled).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
            currentSelectedIds.splice(0);
            this.refreshSelectionInfos();
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.' + (isDisabled ? 'enable' : 'disable'),
        'page.' + this.transPartKey + '.table.' + (isDisabled ? 'enable' : 'disable') + '_confirm',
        'common.dialog.' + (isDisabled ? 'enable' : 'disable'));
    }
  }

  private exchangeApsOverride() {
    if (this.apsManagementEnabled()) {
      const currentSelectedIds = [...this.selectedIds];
      if (this.selectedIds.length > 0) {
        this.setLoading();
        this.allocationService.getWholeOrder(currentSelectedIds[0]).pipe(
          takeUntil(this.destroy$),
          finalize(() => this.setNotLoading())
        ).subscribe(validation => {
          if (validation.success) {
            this.exchangeApsOverrideDialog.open(validation.item);
          } else {
            this.errorMessage(validation.message);
          }
        });
      }
    }
  }

  requestReversal() {
    if (this.validState(OrderTableComponent.ACCEPTED_GIVEUP_STATE)) {
      const currentSelectedIds = [...this.selectedIds];
      this.confirmCommand(() => {
          this.allocationService.requestReversal(currentSelectedIds).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.request_reversal',
        'page.' + this.transPartKey + '.table.request_reversal_confirm');
    }
  }

  cancelReversal() {
    if (this.validState(OrderTableComponent.REVERSAL_REQUESTED_STATE)) {
      const currentSelectedIds = [...this.selectedIds];
      this.confirmCommand(() => {
          this.allocationService.cancelReversal(currentSelectedIds).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.cancel_requested_reversal',
        'page.' + this.transPartKey + '.table.cancel_requested_reversal_confirm');
    }
  }

  showDetail(): void {
    const currentSelectedIds = [...this.selectedIds];
    this.setLoading();
    this.allocationService.getOrderDetail(currentSelectedIds[0]).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.setNotLoading())
    ).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.orderDetailDialog.open(apiValidation.item);
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
  }

  private updateAllocation(selectedIds: string[] = this.selectedIds) {
    this.setLoading();
    const currentSelectedIds = [...selectedIds];
    this.allocationService.getSignificantOrders(currentSelectedIds).pipe(
      finalize(() => this.setNotLoading())
    ).subscribe(validation => {
      if (this.updateAllocationEnabled()) {
        if (validation.success) {
          this.updateAllocationDialog.open(currentSelectedIds, validation.item);
        } else {
          this.errorMessage(validation.message);
        }
      }
    });
  }

  private updateSplitAllocation(selectedIds: string[] = this.selectedIds) {
    this.setLoading();
    const currentSelectedIds = [...selectedIds];
    this.allocationService.getOrderLeg(currentSelectedIds[0]).pipe(
      finalize(() => this.setNotLoading())
    ).subscribe(validation => {
      if (validation.success) {
        this.updateSplitAllocationDialog.open(currentSelectedIds, validation.item);
      } else {
        this.errorMessage(validation.message);
      }
    });
  }

  private updateSplitAllocationEnabled(clearingSelectionInfosByLeg: ClearingSelectionInfo[]) {
    return clearingSelectionInfosByLeg.length == 1
      && this.selectedIds.length <= this.appConfigService.allocationHardLimitCount;
  }

  private updateAllocationEnabled() {
    return this.selectedIds.length > 0
      && this.selectedIds.length <= this.appConfigService.allocationHardLimitCount
      && this.currentClearingSelectionInfosByLeg.length > 0 && !this.hasMultipleOwners();
  }

  private deleteAllocationEnabled() {
    return this.selectedIds.length > 0
      && this.selectedIds.length <= this.appConfigService.allocationHardLimitCount
      && this.currentClearingSelectionInfosByLeg.length > 0;
  }

  private disableOrderEnabled(): boolean {
    let disableOrderEnabled = false;
    // if (this.selectedIds.length > 0 && this.currentClearingSelectionInfos.length > 0) {
    if (this.selectedIds.length > 0 && this.currentClearingSelectionInfosByLeg.length > 0
      && this.selectedIds.length <= this.appConfigService.allocationHardLimitCount) {
      let isDisabled = this.currentClearingSelectionInfosByLeg[0].disabled;
      disableOrderEnabled = true;
      this.currentClearingSelectionInfosByLeg.forEach(value => {
        if (value.unmatchedTotalVolume == 0) disableOrderEnabled = false;
        if (isDisabled != value.disabled) disableOrderEnabled = false;
      })
    }
    return disableOrderEnabled;
  }

  private apsManagementEnabled() {
    // {
    //   if (!o.ExchangeCode.Equals(orders[0].ExchangeCode)) { return false; }
    //   if (!o.Reference.Equals(orders[0].Reference)) { return false; }
    //   if (string.IsNullOrEmpty(o.ApsGroupId)) { return false; }
    //   if ("ACCEPTED GIVEUP".Equals(o.DisplayState)) { return false; }
    if (this.selectedIds.length > this.appConfigService.allocationHardLimitCount) {
      return false;
    }
    const references = [];
    let isAps = true;
    let hasAcceptedGp = false;
    this.currentClearingSelectionInfosByLeg.forEach(value => {
      if (!references.includes(value.reference)) references.push(value.reference);
      if (value.apsGrpTotalVolume != value.totalVolume) isAps = false;
      if (value.state == OrderTableComponent.ACCEPTED_GIVEUP_STATE) hasAcceptedGp = true;
    });
    if (references.length == 1
      && isAps
      && !hasAcceptedGp) {
      const exchField = this.exchanges ? this.exchanges.find(field => field.value
        && this.currentClearingSelectionInfosByLeg.length > 0
        && field.value.clearingHouse === this.currentClearingSelectionInfosByLeg[0].clearingHouse
        && field.value.code === this.currentClearingSelectionInfosByLeg[0].exchangeCode) : undefined;
      return exchField && !exchField.value.apsManageByUser;
    }
    return false;
  }

  @HostListener('document:keydown.delete', ['$event'])
  handleDeleteKeyboardEvent(event: KeyboardEvent) {
    if (this.el.nativeElement.contains(event.target)) {
      if (!this.isReadOnly() && this.deleteAllocationEnabled()) {
        this.markForCheck();
        setTimeout(() => this.deleteOrders());
      }
    }
  }

  onUpdateAllocationSubmit(event: any): void {
    const orderIds = event.formRecord.orderIds;
    this.afterAllocationTask(
      event.taskId,
      () => this.allocationService.getSignificantOrders(orderIds),
      (item) => this.updateAllocationDialog.open(orderIds, item, event.formRecord),
    );
  }

  onSplitAllocationSubmit(event: any): void {
    const orderIds = event.formRecord.orderIds;

    this.afterAllocationTask(
      event.taskId,
      () => this.allocationService.getOrderLeg(orderIds[0]),
      (item) => this.updateSplitAllocationDialog.open(orderIds, item, event.formRecord),
    );
  }

  onExchangeApsOverrideSubmit(event: any): void {
    const orderId = event.formRecord[0].orders[0].id;

    this.afterAllocationTask(
      event.taskId,
      () => this.allocationService.getWholeOrder(orderId),
      (item) => this.exchangeApsOverrideDialog.open(item, event.formRecord),
    );
  }

  refreshContextMenu() {
    this.headerButtonConfig.forEach(menu => {
      if (menu.id === 'disableAllocation') {
        if (menu.enabled()) {
          const isDisabled = this.currentClearingSelectionInfosByLeg[0].disabled;
          menu.tooltip = isDisabled && isDisabled == true ?
            this.getTranslation('page.' + this.transPartKey + '.table.enable_order') :
            this.getTranslation('page.' + this.transPartKey + '.table.disable_order')
        }
      }
    })
    this.tableContextMenuConfig.forEach(menu => {
      if (menu.id === 'deleteAllocation') {
        menu.disabled = !this.deleteAllocationEnabled();
      } else if (menu.id === 'disableAllocation') {
        menu.disabled = !this.disableOrderEnabled();
        if (!menu.disabled) {
          const isDisabled = this.currentClearingSelectionInfosByLeg[0].disabled;
          menu.label = isDisabled && isDisabled == true ?
            this.getTranslation('page.' + this.transPartKey + '.table.enable_order') :
            this.getTranslation('page.' + this.transPartKey + '.table.disable_order')
        }
      } else if (menu.id === 'showDetail') {
        menu.disabled = !this.showDetailEnabled();
      } else if (menu.id === 'updateAllocation') {
        menu.disabled = !this.updateAllocationEnabled();
      } else if (menu.id === 'updateSplitAllocation') {
        menu.disabled = !this.updateSplitAllocationEnabled(this.currentClearingSelectionInfosByLeg);
      } else if (menu.id === 'apsmanagement') {
        menu.disabled = !this.apsManagementEnabled();
        if (!menu.disabled)
          menu.items.forEach(value => {
            if (value.id === 'apsmanagementapproveleg' || value.id === 'apsmanagementapprovewhole' || value.id === 'apsmanagementoverride')
              value.disabled = !this.apsManagementEnabled();
          });
      } else if (menu.id === 'reversalmanagement') {
        menu.disabled = !this.reversalManagementEnabled(OrderTableComponent.ACCEPTED_GIVEUP_STATE, OrderTableComponent.REVERSAL_REQUESTED_STATE);
        if (!menu.disabled)
          menu.items.forEach(value => {
            if (value.id === 'reversalmanagementreqgurev')
              value.disabled = !this.validState(OrderTableComponent.ACCEPTED_GIVEUP_STATE);
            else if (value.id === 'reversalmanagementcancelreqgurev')
              value.disabled = !this.validState(OrderTableComponent.REVERSAL_REQUESTED_STATE);
          });
      }
    });
    this.tableContextMenuConfig = [...this.tableContextMenuConfig];
    // this.detectChanges();
  }

  rowDblClick() {
    if (!this.isReadOnly()) {
      this.setLoading();
      // this.tableLoadingCount++;
      const request: ClearingInfoLoadRequest = new ClearingInfoLoadRequest([...this.ccTable.selectedIds]);

      this.getSelectionInfoLegs(request).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
        if (this.updateAllocationEnabled()) {
          if (apiValidation.success) {
            if (this.updateSplitAllocationEnabled(apiValidation.item)) {
              this.updateSplitAllocation(request.itemIds);
            } else {
              this.updateAllocation(request.itemIds);
            }
          }
        } else {
          this.showDetail();
        }
        this.setNotLoading();
        // this.tableLoadingCount--;
      });
    } else if (this.showDetailEnabled()) {
      this.showDetail();
    }
  }

  ctrlShift() {
    if (!this.isReadOnly()
      && (this.updateAllocationEnabled() || this.updateSplitAllocationEnabled(this.currentClearingSelectionInfosByLeg))) {
      this.updateAllocation();
    }
  }

  protected getSelectionSummary(summaryRequest: ClearingInfoLoadRequest) {
    return this.clearingOrderInfoService.getSelectionSummary(summaryRequest);
  }

  protected getSelectionInfoLegs(request: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionInfo[]>> {
    return this.clearingOrderInfoService.getSelectionInfoLegs(request);
  }

}
